import {
  ArrowLeft,
  Building2,
  Globe,
  Heart,
  Mail,
  MapPin,
  Phone,
  Radius,
  User,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PrecisionNote } from "@/components/ui/precision-note";
import type { ApiError } from "@/lib/api/unwrap";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import {
  useAddFavorite,
  useFavoritesQuery,
  useProfileQuery,
  useRemoveFavorite,
} from "../api/hooks";
import type { ExpertProfileDetail, IssueRef } from "../types";
import { Badge } from "./ui/Badge";
import { EmptyState, ErrorState, Loading } from "./ui/states";

/** A thematic-coloured issue chip. */
function IssueChip({ issue }: { issue: IssueRef }) {
  const theme = thematicClasses(issue.css_class);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold",
        theme.softBg,
        theme.ink,
      )}
    >
      {issue.name}
    </span>
  );
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  if (value == null || value === "") return null;
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

/**
 * A row of catalogue chips, optionally followed by the facet's free-text
 * précision. The précision is prose, not an option, so it reads as a labelled
 * line below the chips — never as one more chip.
 */
function ChipRow({
  label,
  values,
  precision,
  icon,
}: {
  label: string;
  values: string[];
  precision?: string | null;
  /** Drawn inside every chip, as the region rows mark theirs. */
  icon?: ReactNode;
}) {
  if (values.length === 0 && !precision?.trim()) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </h3>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex max-w-full items-center gap-1.5 break-words rounded-md bg-muted px-2.5 py-1 text-sm text-foreground"
            >
              {icon}
              {v}
            </span>
          ))}
        </div>
      )}
      <PrecisionNote text={precision} />
    </div>
  );
}

/** One row of the Coordonnées sidebar. */
function CoordRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  if (!children) return null;
  return (
    <div className="flex gap-3 border-b border-border/60 py-2.5 last:border-b-0">
      <span className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="break-words text-sm">{children}</div>
      </div>
    </div>
  );
}

export function ProfileView({
  expertId,
  onBack,
  action,
}: {
  expertId: string;
  onBack: () => void;
  /** When set (opened from an action's directory), the hero shows the ♥ toggle. */
  action?: string;
}) {
  const { t } = useTranslation();
  const {
    data: profile,
    isPending,
    isError,
    error,
    refetch,
  } = useProfileQuery(expertId, action ?? null);
  // A 404 (missing / unauthorized / non-consenting profile) is terminal — show
  // the "not available" state with no retry; only offer retry for transient
  // (5xx / network) failures.
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? (error as ApiError).status
      : 0;
  const notFound = status === 404;
  const favorites = useFavoritesQuery(action ?? null);
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();
  const [toggling, setToggling] = useState(false);

  // ♥ only when the server says the action context allows it (an action whose
  // thematic or own flag allows experts, as on the Django page) AND the
  // favorites list loaded (it 403s without an enterprise — an expert
  // self-preview).
  const canFavorite =
    Boolean(action) && Boolean(profile?.can_favorite) && !favorites.isError;
  const favRow = (favorites.data ?? []).find((f) => f.expert_profile === expertId);
  const isFavorite = Boolean(favRow);

  const toggleFavorite = async () => {
    if (!action) return;
    setToggling(true);
    try {
      if (favRow) await removeFavorite.mutateAsync(favRow.id);
      else await addFavorite.mutateAsync({ action, expertProfileId: expertId });
    } catch {
      toast.error(t("expert.error"));
    } finally {
      setToggling(false);
    }
  };

  const favoriteControl = canFavorite ? (
    <button
      type="button"
      aria-pressed={isFavorite}
      aria-label={isFavorite ? t("expert.card.unfavorite") : t("expert.card.favorite")}
      disabled={toggling}
      onClick={() => void toggleFavorite()}
      className={cn(
        "ml-auto grid size-10 shrink-0 place-items-center rounded-lg border transition-colors",
        isFavorite
          ? "border-destructive/30 bg-destructive/10"
          : "border-border bg-background hover:bg-accent",
        toggling && "opacity-60",
      )}
    >
      <Heart
        className={cn(
          "size-5",
          isFavorite ? "fill-destructive text-destructive" : "text-muted-foreground",
        )}
        aria-hidden
      />
    </button>
  ) : null;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="size-4" aria-hidden />
        {t("expert.profile.back")}
      </Button>

      {isPending ? (
        <Loading />
      ) : isError && notFound ? (
        <EmptyState title={t("expert.profile.notAvailable")} />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : !profile ? (
        <EmptyState title={t("expert.profile.notAvailable")} />
      ) : (
        <ProfileBody profile={profile} favoriteControl={favoriteControl} />
      )}
    </div>
  );
}

/**
 * The profile detail, reused by the public profile view and the expert
 * dashboard. `showContactCta` hides the "contact this expert" mailto on the
 * dashboard (it's the expert's own profile).
 *
 * `showMandates` defaults to true and no caller passes false — the comment here
 * used to claim the dashboard turned it off and rendered mandates from a
 * dedicated endpoint, which stopped being true at some point. It mattered:
 * mandates name their enjeu, and that sentence was exactly what would persuade
 * a reader that this component could not leak a sector-withheld enjeu to the
 * expert. The filtering now happens server-side in `publicly_visible_mandates`,
 * which is where it belongs, and does not depend on this flag.
 */
export function ProfileBody({
  profile,
  showMandates = true,
  showContactCta = true,
  favoriteControl = null,
}: {
  profile: ExpertProfileDetail;
  showMandates?: boolean;
  showContactCta?: boolean;
  favoriteControl?: ReactNode;
}) {
  const { t } = useTranslation();

  const contactName = `${profile.contact_primary_first_name ?? ""} ${
    profile.contact_primary_last_name ?? ""
  }`.trim();
  const coveredIssues = profile.expertises?.map((e) => e.issue) ?? [];
  // The hero caps both region lists at four; the full lists stay in the scope
  // section further down.
  const heroOriginRegions = (profile.origin_regions ?? []).slice(0, 4);
  const heroExtraOriginRegions = Math.max(
    0,
    (profile.origin_regions?.length ?? 0) - 4,
  );
  const heroServedRegions = (profile.service_regions ?? []).slice(0, 4);
  const heroExtraServedRegions = Math.max(
    0,
    (profile.service_regions?.length ?? 0) - 4,
  );
  const avatarTheme = thematicClasses(coveredIssues[0]?.css_class);
  const initials = (
    contactName
      ? `${profile.contact_primary_first_name?.[0] ?? ""}${profile.contact_primary_last_name?.[0] ?? ""}`
      : profile.organization_name.slice(0, 2)
  ).toUpperCase();

  const sectors = (profile.sectors_served ?? []).map((s) => s.name);
  const originRegions = (profile.origin_regions ?? []).map((r) => r.name);
  const servedRegions = (profile.service_regions ?? []).map((r) => r.name);

  const phone = profile.contact_phone
    ? `${profile.contact_phone}${
        profile.contact_primary_extension
          ? ` ${t("expert.profile.extension", { ext: profile.contact_primary_extension })}`
          : ""
      }`
    : "";

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex-row items-start gap-4 space-y-0">
            <div
              className={cn(
                "grid size-14 shrink-0 place-items-center rounded-2xl text-xl font-bold",
                avatarTheme.softBg,
                avatarTheme.ink,
              )}
              aria-hidden
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <CardTitle className="text-2xl">{profile.organization_name}</CardTitle>
              {contactName && (
                <p className="text-sm text-muted-foreground">
                  {contactName}
                  {profile.contact_primary_function
                    ? ` · ${profile.contact_primary_function}`
                    : ""}
                </p>
              )}
              {/* Same datum as the directory card's building: where the expert
                  is established. */}
              {heroOriginRegions.length > 0 && (
                <p
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
                  title={t("expert.profile.originRegions")}
                >
                  <Building2 className="size-4" aria-hidden />
                  <span>
                    {heroOriginRegions.map((r) => r.name).join(", ")}
                    {heroExtraOriginRegions > 0 && (
                      <span className="italic">
                        {" "}
                        {t("expert.card.moreRegions", {
                          count: heroExtraOriginRegions,
                        })}
                      </span>
                    )}
                  </span>
                </p>
              )}
              {heroServedRegions.length > 0 && (
                <p
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
                  title={t("expert.profile.servedRegions")}
                >
                  <Radius className="size-4" aria-hidden />
                  <span>
                    {heroServedRegions.map((r) => r.name).join(", ")}
                    {heroExtraServedRegions > 0 && (
                      <span className="italic">
                        {" "}
                        {t("expert.card.moreRegions", {
                          count: heroExtraServedRegions,
                        })}
                      </span>
                    )}
                  </span>
                </p>
              )}
            </div>
            {favoriteControl}
          </CardHeader>
          {profile.description && (
            <CardContent>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("expert.profile.presentation")}
              </h3>
              <p className="text-sm leading-relaxed">{profile.description}</p>
            </CardContent>
          )}
        </Card>

        {coveredIssues.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("expert.profile.coveredIssues")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {coveredIssues.map((issue) => (
                <IssueChip key={issue.id} issue={issue} />
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("expert.profile.overview")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Detail label={t("expert.profile.founded")} value={profile.founding_year} />
              <Detail
                label={t("expert.profile.ddStart")}
                value={profile.dd_services_start_year}
              />
              <Detail
                label={t("expert.profile.employees")}
                value={profile.total_employees}
              />
              <Detail
                label={t("expert.profile.dedicated")}
                value={profile.dedicated_experts_count}
              />
              <Detail label={t("expert.profile.awards")} value={profile.awards} />
              <Detail
                label={t("expert.profile.affiliations")}
                value={profile.affiliations}
              />
            </dl>
            <ChipRow
              label={t("expert.profile.serviceTypes")}
              values={profile.service_types_display ?? []}
              precision={profile.service_types_other}
            />
            <ChipRow
              label={t("expert.profile.sectors")}
              values={sectors}
              precision={profile.sectors_served_other}
            />
            <ChipRow
              label={t("expert.profile.originRegions")}
              values={originRegions}
              icon={<Building2 className="size-3.5 shrink-0" aria-hidden />}
            />
            <ChipRow
              label={t("expert.profile.servedRegions")}
              values={servedRegions}
              icon={<Radius className="size-3.5 shrink-0" aria-hidden />}
            />
            <ChipRow
              label={t("expert.profile.orgTypes")}
              values={profile.organization_types_served_display ?? []}
              precision={profile.organization_types_served_other}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("expert.profile.expertises")}</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.expertises?.length ? (
              <ul className="space-y-3">
                {profile.expertises.map((exp) => {
                  const theme = thematicClasses(exp.issue.css_class);
                  return (
                    <li
                      key={exp.id}
                      className={cn(
                        "rounded-md border border-l-4 border-border p-3",
                        theme.border,
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn("font-semibold", theme.ink)}>
                          {exp.issue.name}
                        </span>
                        <Badge tone="neutral">
                          {t("expert.profile.mandateCount", {
                            count: exp.mandate_count,
                          })}
                        </Badge>
                      </div>
                      {exp.goals?.length > 0 && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {t("expert.profile.goals")}:{" "}
                          </span>
                          {exp.goals.map((g) => g.name).join(", ")}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("expert.profile.noExpertises")}
              </p>
            )}
          </CardContent>
        </Card>

        {showMandates && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("expert.profile.mandates")}</CardTitle>
            </CardHeader>
            <CardContent>
              {profile.mandates?.length ? (
                <ul className="space-y-3">
                  {profile.mandates.map((m) => (
                    <li key={m.id} className="rounded-md border border-border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{m.name}</span>
                        <Badge tone="neutral">{m.issue.name}</Badge>
                      </div>
                      {m.description && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {m.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("expert.profile.noMandates")}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Coordonnées sidebar */}
      <Card className="lg:sticky lg:top-20">
        <CardHeader>
          <CardTitle className="text-lg">{t("expert.profile.contact")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          <CoordRow icon={<MapPin className="size-4" />} label={t("expert.profile.address")}>
            {profile.head_office_address}
          </CoordRow>
          <CoordRow icon={<Mail className="size-4" />} label={t("expert.profile.email")}>
            {profile.contact_email && (
              <a
                href={`mailto:${profile.contact_email}`}
                className="font-medium text-theme-economic-ink hover:underline"
              >
                {profile.contact_email}
              </a>
            )}
          </CoordRow>
          <CoordRow icon={<Phone className="size-4" />} label={t("expert.profile.phone")}>
            {phone}
          </CoordRow>
          <CoordRow icon={<Globe className="size-4" />} label={t("expert.profile.website")}>
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-theme-economic-ink hover:underline"
              >
                {profile.website}
              </a>
            )}
          </CoordRow>
          <CoordRow
            icon={<User className="size-4" />}
            label={t("expert.profile.contactPerson")}
          >
            {contactName}
          </CoordRow>

          {showContactCta && profile.contact_email && (
            <a
              href={`mailto:${profile.contact_email}`}
              className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Mail className="size-4" aria-hidden />
              {t("expert.profile.contactCta")}
            </a>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
